/** Pôr no mapa um modelo que só tem a latitude e a longitude do IfcSite.
 *
 *  O ficheiro declara onde está a origem do sítio (latitude, longitude,
 *  elevação) e, no contexto, para onde aponta o norte verdadeiro
 *  (TrueNorth). Para desenhar sobre o mapa é preciso um CRS projectado;
 *  usa-se o WGS 84 / UTM do fuso onde a longitude cai. O CRS não muda o
 *  sítio onde o modelo fica, só a grelha em que se escreve; mas é
 *  calculado, não declarado, e diz-se isso.
 *
 *  Rotação: o eixo X do ficheiro faz com o leste verdadeiro o ângulo do
 *  TrueNorth (graus a partir de +Y, sentido horário; sem TrueNorth, +Y é
 *  o norte, como manda a norma). Na grelha UTM soma-se a convergência
 *  (o ângulo entre o norte verdadeiro e o norte da quadrícula naquele
 *  ponto), que se calcula pelo proj4. */

import type { GeoPlacement } from '@viewer/lib/geoTransform'
import { epsgUtmWgs84, resolveProj4Def } from '@viewer/map/crsDefs'
import { loadProj4 } from '@viewer/map/proj4Lazy'
import type { LocalizacaoDoSitio } from './leituraDoMapa'

export interface ColocacaoPeloSitio {
  epsg: string
  placement: GeoPlacement
  /** Ângulo do norte verdadeiro em relação ao norte da quadrícula, em
   *  graus, sentido anti-horário (calculado). */
  convergenciaGraus: number
}

/** A matemática, separada do carregamento do proj4 para se testar. */
export function calcularColocacao(
  s: LocalizacaoDoSitio,
  epsg: string,
  paraGrelha: (lonLat: [number, number]) => [number, number],
): ColocacaoPeloSitio {
  const [e, n] = paraGrelha([s.longitude, s.latitude])
  // Um passo pequeno para norte: a direcção do norte verdadeiro na grelha.
  const [e2, n2] = paraGrelha([s.longitude, s.latitude + 1e-4])
  const convergenciaGraus = (Math.atan2(-(e2 - e), n2 - n) * 180) / Math.PI
  return {
    epsg,
    convergenciaGraus,
    placement: {
      eastings: e,
      northings: n,
      orthogonalHeight: s.elevacaoMetros ?? 0,
      rotationDeg: (s.trueNorthGraus ?? 0) + convergenciaGraus,
      scale: 1,
    },
  }
}

/** O que o viewer regista para um modelo posto no mapa pelo IfcSite: o
 *  placement e o CRS em que ele está escrito. O CRS é sempre o da grelha
 *  calculada, mesmo que o ficheiro nomeie outro (um IfcProjectedCRS sem
 *  IfcMapConversion): o placement só tem sentido nessa grelha. Com o CRS
 *  do ficheiro, o mapa lia coordenadas UTM como se fossem dele (Lisboa
 *  aparecia a 77° N). */
export function registoDaColocacao(c: ColocacaoPeloSitio): { crs: string; placement: GeoPlacement } {
  return { crs: c.epsg, placement: c.placement }
}

export async function colocacaoPeloSitio(s: LocalizacaoDoSitio): Promise<ColocacaoPeloSitio | null> {
  const epsg = epsgUtmWgs84(s.latitude, s.longitude)
  const def = await resolveProj4Def(epsg)
  if (!def) return null
  const proj4 = await loadProj4()
  const conv = proj4('WGS84', def)
  return calcularColocacao(s, epsg, (p) => conv.forward(p) as [number, number])
}
